import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { CornerUpLeft, Sparkles } from "lucide-react";
import { openAsk } from "./ChatPanel";
import { getIndustry, getSkill, getTechnology } from "../lib/queries";
import { TECH_CAT_HUE } from "../lib/visuals";

export function BackBar({ to, label }: { to: string; label: string }) {
  return (
    <Link to={to} className="backbar">
      <CornerUpLeft size={15} /> {label}
    </Link>
  );
}

export function TagRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="tagrow">
      <span className="eyebrow tagrow-label">{label}</span>
      <div className="wrapflex" style={{ gap: 7 }}>
        {children}
      </div>
    </div>
  );
}

export function AskAbout({ prompt }: { prompt: string }) {
  return (
    <button
      type="button"
      className="btn btn-primary btn-sm"
      onClick={() => openAsk(prompt)}
      style={{ height: "40px", borderRadius: "25px" }}
    >
      <Sparkles size={15} /> Ask AI
    </button>
  );
}

export function paragraphs(text: string) {
  return text.split(/\n\s*\n+/).map((p, i) => <p key={i}>{p.trim()}</p>);
}

export function NotFound() {
  return (
    <div className="wrap-narrow section" style={{ minHeight: "40vh" }}>
      <p className="eyebrow">Not found</p>
      <h1 className="display" style={{ fontSize: 40, marginTop: 10 }}>
        There's nothing at this address.
      </h1>
      <div style={{ marginTop: 22 }}>
        <BackBar to="/" label="Back to home" />
      </div>
    </div>
  );
}

export function Chip({
  children,
  hue,
  mono = false,
  className,
  to,
  title,
}: {
  children: ReactNode;
  hue?: string;
  mono?: boolean;
  className?: string;
  to?: string;
  title?: string;
}) {
  const cls = ["chip", mono ? "chip-mono" : "", className ?? ""].filter(Boolean).join(" ");
  const inner = (
    <>
      {hue && <span className="dot" style={{ background: hue }} />}
      {children}
    </>
  );
  if (to) {
    return (
      <Link to={to} className={cls} title={title}>
        {inner}
      </Link>
    );
  }
  return (
    <span className={cls} title={title}>
      {inner}
    </span>
  );
}

export function IndustryChip({ id, to, count }: { id: string; to?: string; count?: number }) {
  const ind = getIndustry(id);
  if (!ind) return null;
  return (
    <Chip mono hue="var(--hue)" className={`hue-${id}`} to={to ?? `/by/industry?industry=${id}`}>
      {ind.label}
      {count != null ? ` · ${count}` : ""}
    </Chip>
  );
}

export function TechChip({ id, to }: { id: string; to?: string }) {
  const t = getTechnology(id);
  if (!t) return null;
  const hue = (t.category && TECH_CAT_HUE[t.category]) || "var(--accent)";
  return (
    <Chip mono hue={hue} to={to ?? `/by/technology?tech=${id}`}>
      {t.label}
    </Chip>
  );
}

export function SkillChip({ id, to }: { id: string; to?: string }) {
  const s = getSkill(id);
  if (!s) return null;
  return <Chip to={to ?? `/by/skill?skill=${id}`}>{s.label}</Chip>;
}
